import { useEffect, useState } from 'react';
import { hasSupabaseConfig, supabase } from '../../../shared/api/supabaseClient.js';
import {
  loadStoredBlablaLinkProfileAvatar,
  syncBlablaLinkProfileAvatar,
} from '../api/blablalinkProfileAvatarApi.js';

const avatarCacheByUser = new Map();
const lastSyncAtByUser = new Map();
const syncPromiseByUser = new Map();

async function readAuthenticatedUserId() {
  if (!hasSupabaseConfig || !supabase) return '';
  const { data: { user }, error } = await supabase.auth.getUser();
  if (error || !user?.id) return '';
  return String(user.id);
}

function runSync(userId, force = false) {
  const cachedAvatar = avatarCacheByUser.get(userId) || null;
  const lastSyncAt = Number(lastSyncAtByUser.get(userId) || 0);
  if (!force && cachedAvatar && Date.now() - lastSyncAt < 60_000) {
    return Promise.resolve(cachedAvatar);
  }

  const existingPromise = syncPromiseByUser.get(userId);
  if (existingPromise) return existingPromise;

  const syncPromise = syncBlablaLinkProfileAvatar()
    .then((avatar) => {
      avatarCacheByUser.set(userId, avatar);
      lastSyncAtByUser.set(userId, Date.now());
      return avatar;
    })
    .finally(() => {
      syncPromiseByUser.delete(userId);
    });

  syncPromiseByUser.set(userId, syncPromise);
  return syncPromise;
}

export function useBlablaLinkProfileAvatar() {
  const [avatar, setAvatar] = useState(null);

  useEffect(() => {
    let active = true;
    let currentUserId = '';

    const refresh = async (force = false) => {
      const userId = await readAuthenticatedUserId();
      if (!active) return;
      currentUserId = userId;

      if (!userId) {
        setAvatar(null);
        return;
      }

      const cached = avatarCacheByUser.get(userId) || null;
      if (cached) setAvatar(cached);

      try {
        const stored = await loadStoredBlablaLinkProfileAvatar();
        if (active && currentUserId === userId && stored) {
          avatarCacheByUser.set(userId, stored);
          setAvatar(stored);
        }
      } catch {
        // Avatar loading is optional; the UI keeps the nickname fallback.
      }

      try {
        const synced = await runSync(userId, force);
        if (active && currentUserId === userId && synced) setAvatar(synced);
      } catch {
        // BlablaLink may be temporarily unavailable. Keep the stored image or fallback.
      }
    };

    refresh(false);
    const handleProfileChanged = () => refresh(true);
    window.addEventListener('dororaid:game-profile-changed', handleProfileChanged);
    return () => {
      active = false;
      window.removeEventListener('dororaid:game-profile-changed', handleProfileChanged);
    };
  }, []);

  return avatar;
}
